import { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { RequestErrorType } from '@core/utils/api-axios-instance'
import {
  isMFAExpiredTokenError,
  isMFAInvalidTokenError,
  isMFARequiredError,
  isMFASessionNotFoundError,
  isMFASkippedSessionExpiredError,
} from '@core/authentication'
import { getMFATokenStandalone, clearMFATokenStandalone } from './useMFAToken'

/**
 * Registers the MFA interceptors with an Axios instance for web application
 *
 * @param axiosInstance The Axios instance to register the interceptors with
 * @param onMFARequired Callback function to be called when MFA is required
 * @param onMFAExpired Callback function to be called when MFA token expires
 */
export const registerWebMFAInterceptors = (
  axiosInstance: AxiosInstance,
  onMFARequired: () => void,
  onMFAExpired: () => void,
) => {
  // Request interceptor to add MFA token to headers
  axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Only add MFA token if we're in the browser (not SSR)
      if (typeof window !== 'undefined') {
        const mfaToken = getMFATokenStandalone()

        if (mfaToken) {
          config.headers.set('X-MFA-TOKEN', mfaToken)
        }
      }

      return config
    },
    (error) => {
      return Promise.reject(error)
    },
  )

  // Response interceptor to catch MFA required errors
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error: RequestErrorType<{ errorType: string }>) => {
      // Only handle MFA errors in browser environment
      if (typeof window === 'undefined') {
        return Promise.reject(error)
      }

      const errorData = error.axiosError?.response?.data

      // Handle expired/missing MFA token error (400)
      if (
        isMFAExpiredTokenError(errorData?.errorType) ||
        isMFASkippedSessionExpiredError(errorData?.errorType)
      ) {
        // Call the onMFAExpired callback to trigger the expired MFA flow
        onMFAExpired()

        // Return a rejected promise to stop the error propagation
        return Promise.reject(error)
      }

      // Handle MFA required error (401)
      if (
        isMFARequiredError(errorData?.errorType) ||
        isMFASessionNotFoundError(errorData?.errorType)
      ) {
        // Call the onMFARequired callback
        onMFARequired()

        // Return a rejected promise to stop the error propagation
        return Promise.reject(error)
      }

      // Handle MFA invalid token error (400)
      if (isMFAInvalidTokenError(errorData?.errorType)) {
        // Clear the MFA token
        clearMFATokenStandalone()

        // Call the onMFARequired callback
        onMFARequired()

        // Return a rejected promise to stop the error propagation
        return Promise.reject(error)
      }

      // For other errors, just propagate
      return Promise.reject(error)
    },
  )
}
